/**
 * Leviathan - Delivery Program Form Page
 * Quasar Prime LLC
 *
 * Create and edit delivery programs
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { parseApiError } from '@/utils/errorHandler';
import {
  Loader2,
  ArrowLeft,
  Gauge,
  Phone,
  Calendar,
  Save,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface Customer {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  sku: string;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const PROGRAM_TYPES = [
  {
    value: 'KEEP_FULL',
    label: 'Keep Full',
    icon: Gauge,
    description: 'Auto-deliver when tank drops below trigger level',
  },
  {
    value: 'WILL_CALL',
    label: 'Will Call',
    icon: Phone,
    description: 'Customer calls to request delivery',
  },
  {
    value: 'SCHEDULED',
    label: 'Scheduled',
    icon: Calendar,
    description: 'Fixed recurring delivery schedule',
  },
];

export default function DeliveryProgramForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState({
    customerId: searchParams.get('customerId') || '',
    customerName: '',
    locationId: '',
    locationName: '',
    locationAddress: '',
    programType: 'KEEP_FULL',
    productId: '',
    productName: '',
    tankCapacityGallons: '',
    currentTankLevel: '',
    triggerPercentage: '30',
    targetPercentage: '90',
    scheduleFrequency: 'WEEKLY',
    scheduleDayOfWeek: '1',
    scheduleDayOfMonth: '1',
    customScheduleDays: '14',
    notes: '',
  });

  const [error, setError] = useState<string | null>(null);

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/customers?limit=200&isActive=true`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch customers');
      return res.json();
    },
  });

  // Fetch products
  const { data: productsData } = useQuery({
    queryKey: ['products-list'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/inventory?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch products');
      return res.json();
    },
  });

  // Fetch existing program if editing
  const { data: programData, isLoading: programLoading } = useQuery({
    queryKey: ['delivery-program', id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch program');
      return res.json();
    },
    enabled: isEditing,
  });

  // Populate form when editing
  useEffect(() => {
    if (programData?.program) {
      const p = programData.program;
      setFormData({
        customerId: p.customerId,
        customerName: p.customerName,
        locationId: p.locationId,
        locationName: p.locationName,
        locationAddress: p.locationAddress || '',
        programType: p.programType,
        productId: p.productId,
        productName: p.productName,
        tankCapacityGallons: p.tankCapacityGallons.toString(),
        currentTankLevel: p.currentTankLevel?.toString() || '',
        triggerPercentage: p.triggerPercentage?.toString() || '30',
        targetPercentage: p.targetPercentage?.toString() || '90',
        scheduleFrequency: p.scheduleFrequency || 'WEEKLY',
        scheduleDayOfWeek: p.scheduleDayOfWeek?.toString() || '1',
        scheduleDayOfMonth: p.scheduleDayOfMonth?.toString() || '1',
        customScheduleDays: p.customScheduleDays?.toString() || '14',
        notes: p.notes || '',
      });
    }
  }, [programData]);

  // Update customer name when customer changes
  useEffect(() => {
    if (formData.customerId && customersData?.items) {
      const customer = customersData.items.find((c: Customer) => c.id === formData.customerId);
      if (customer) {
        setFormData((prev) => ({
          ...prev,
          customerName: customer.name,
          locationName: customer.name, // Default location name to customer name
        }));
      }
    }
  }, [formData.customerId, customersData]);

  // Update product name when product changes
  useEffect(() => {
    if (formData.productId && productsData?.items) {
      const product = productsData.items.find((p: Product) => p.id === formData.productId);
      if (product) {
        setFormData((prev) => ({ ...prev, productName: product.name }));
      }
    }
  }, [formData.productId, productsData]);

  const customers: Customer[] = customersData?.items || [];
  const products: Product[] = productsData?.items || [];

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        customerId: data.customerId,
        customerName: data.customerName,
        locationId: data.locationId || data.customerId,
        locationName: data.locationName,
        locationAddress: data.locationAddress,
        programType: data.programType,
        productId: data.productId,
        productName: data.productName,
        tankCapacityGallons: parseInt(data.tankCapacityGallons),
        currentTankLevel: data.currentTankLevel ? parseInt(data.currentTankLevel) : undefined,
        triggerPercentage:
          data.programType === 'KEEP_FULL' ? parseInt(data.triggerPercentage) : undefined,
        targetPercentage:
          data.programType === 'KEEP_FULL' ? parseInt(data.targetPercentage) : undefined,
        scheduleFrequency: data.programType === 'SCHEDULED' ? data.scheduleFrequency : undefined,
        scheduleDayOfWeek:
          data.programType === 'SCHEDULED' &&
          (data.scheduleFrequency === 'WEEKLY' || data.scheduleFrequency === 'BIWEEKLY')
            ? parseInt(data.scheduleDayOfWeek)
            : undefined,
        scheduleDayOfMonth:
          data.programType === 'SCHEDULED' && data.scheduleFrequency === 'MONTHLY'
            ? parseInt(data.scheduleDayOfMonth)
            : undefined,
        customScheduleDays:
          data.programType === 'SCHEDULED' && data.scheduleFrequency === 'CUSTOM'
            ? parseInt(data.customScheduleDays)
            : undefined,
        notes: data.notes || undefined,
      };

      const url = isEditing
        ? `${API_URL}/api/delivery-programs/${id}`
        : `${API_URL}/api/delivery-programs`;

      const res = await fetch(url, {
        method: isEditing ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorMessage = await parseApiError(res);
        throw new Error(errorMessage);
      }
      return res.json();
    },
    onSuccess: (data) => {
      navigate(`/operations/delivery-programs/${data.program.id}`);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.customerId || !formData.productId || !formData.tankCapacityGallons) {
      setError('Customer, Product, and Tank Capacity are required');
      return;
    }

    saveMutation.mutate(formData);
  };

  if (isEditing && programLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Link
              to={isEditing ? `/operations/delivery-programs/${id}` : '/operations/delivery-programs'}
              className="text-slate-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-slate-100">
              {isEditing ? 'Edit Delivery Program' : 'New Delivery Program'}
            </h1>
          </div>
          <p className="text-slate-400 text-sm ml-9">
            {isEditing ? 'Update program settings' : 'Set up automated delivery for a customer'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer & Product */}
        <Panel title="Customer & Product">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Customer *</label>
              <select
                value={formData.customerId}
                onChange={(e) => setFormData((prev) => ({ ...prev, customerId: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                <option value="">Select customer...</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Product *</label>
              <select
                value={formData.productId}
                onChange={(e) => setFormData((prev) => ({ ...prev, productId: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              >
                <option value="">Select product...</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} ({product.sku})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Location Name</label>
              <input
                type="text"
                value={formData.locationName}
                onChange={(e) => setFormData((prev) => ({ ...prev, locationName: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="e.g. Main Facility, North Warehouse"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Location Address</label>
              <input
                type="text"
                value={formData.locationAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, locationAddress: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
          </div>
        </Panel>

        {/* Program Type */}
        <Panel title="Program Type">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PROGRAM_TYPES.map((type) => {
              const TypeIcon = type.icon;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, programType: type.value }))}
                  className={`p-4 border rounded-lg text-left transition-colors ${
                    formData.programType === type.value
                      ? 'bg-amber-500/10 border-amber-500'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <TypeIcon
                    className={`w-6 h-6 mb-2 ${
                      formData.programType === type.value ? 'text-amber-400' : 'text-slate-400'
                    }`}
                  />
                  <div className="text-slate-100 font-semibold">{type.label}</div>
                  <div className="text-slate-500 text-xs mt-1">{type.description}</div>
                </button>
              );
            })}
          </div>
        </Panel>

        {/* Tank Settings */}
        <Panel title="Tank Settings">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Tank Capacity (gallons) *</label>
              <input
                type="number"
                value={formData.tankCapacityGallons}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, tankCapacityGallons: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                placeholder="10000"
                required
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm mb-2">Current Tank Level (%)</label>
              <input
                type="number"
                value={formData.currentTankLevel}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, currentTankLevel: e.target.value }))
                }
                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                min="0"
                max="100"
                placeholder="50"
              />
            </div>
          </div>
        </Panel>

        {/* Keep-Full Settings */}
        {formData.programType === 'KEEP_FULL' && (
          <Panel title="Keep-Full Settings">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Trigger Level (%)</label>
                <input
                  type="number"
                  value={formData.triggerPercentage}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, triggerPercentage: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  min="5"
                  max="50"
                />
                <p className="text-slate-500 text-xs mt-1">Deliver when tank drops below this %</p>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-2">Target Level (%)</label>
                <input
                  type="number"
                  value={formData.targetPercentage}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, targetPercentage: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  min="50"
                  max="100"
                />
                <p className="text-slate-500 text-xs mt-1">Fill to this %</p>
              </div>
            </div>
          </Panel>
        )}

        {/* Scheduled Settings */}
        {formData.programType === 'SCHEDULED' && (
          <Panel title="Schedule Settings">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-slate-400 text-sm mb-2">Frequency</label>
                <select
                  value={formData.scheduleFrequency}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, scheduleFrequency: e.target.value }))
                  }
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                >
                  <option value="WEEKLY">Weekly</option>
                  <option value="BIWEEKLY">Every 2 Weeks</option>
                  <option value="MONTHLY">Monthly</option>
                  <option value="CUSTOM">Custom Interval</option>
                </select>
              </div>

              {(formData.scheduleFrequency === 'WEEKLY' ||
                formData.scheduleFrequency === 'BIWEEKLY') && (
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Day of Week</label>
                  <select
                    value={formData.scheduleDayOfWeek}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduleDayOfWeek: e.target.value }))
                    }
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  >
                    {DAY_NAMES.map((day, index) => (
                      <option key={index} value={index}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.scheduleFrequency === 'MONTHLY' && (
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Day of Month</label>
                  <select
                    value={formData.scheduleDayOfMonth}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, scheduleDayOfMonth: e.target.value }))
                    }
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.scheduleFrequency === 'CUSTOM' && (
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Interval (days)</label>
                  <input
                    type="number"
                    value={formData.customScheduleDays}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, customScheduleDays: e.target.value }))
                    }
                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    min="1"
                    max="365"
                  />
                </div>
              )}
            </div>
          </Panel>
        )}

        {/* Notes */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
            rows={3}
            placeholder="Special instructions or notes..."
          />
        </Panel>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Link
            to={isEditing ? `/operations/delivery-programs/${id}` : '/operations/delivery-programs'}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 px-6 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Update Program' : 'Create Program'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-slate-100 mb-6 pb-3 border-b border-slate-700">
        {title}
      </h3>
      {children}
    </div>
  );
}
